import { useState, type ChangeEvent } from 'react';
import {
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  type SelectChangeEvent,
} from '@mui/material';

import {
  IntervalValues,
  isDeclineRecurringAction,
  type NodeItem,
  type RetryInterval,
} from '@models/flows';

import { useToast } from '@hooks/useToast';

import s from './RecurringActionNodeConfiguration.module.css';
type RecurringActionNodeConfigurationProps = {
  currentNode: NodeItem;
  onChange: (data: NodeItem) => void;
};

const RETRY_INTERVAL_OPTIONS = [
  { label: 'Days', value: IntervalValues.days },
  { label: 'Hours', value: IntervalValues.hours },
  { label: 'Minutes', value: IntervalValues.minutes },
];

const ACTION_OPTIONS = [
  { label: 'Retry', value: 'retry' },
  { label: 'Decline', value: 'decline' },
];

const getSelectedIntervalValue = (currentNode: NodeItem) => {
  let intervalValue = IntervalValues.days;

  if (currentNode.retryInterval?.hours && currentNode.retryInterval.hours > 0) {
    intervalValue = IntervalValues.hours;
  }
  if (currentNode.retryInterval?.minutes && currentNode.retryInterval.minutes > 0) {
    intervalValue = IntervalValues.minutes;
  }

  return intervalValue;
};

function RecurringActionNodeConfiguration({
  currentNode,
  onChange,
}: RecurringActionNodeConfigurationProps) {
  const [selectedRetryInterval, setSelectedRetryInterval] = useState(
    getSelectedIntervalValue(currentNode),
  );
  const isDisabled = isDeclineRecurringAction(currentNode.action || '');
  const toast = useToast();

  if (!currentNode) return null;
  const { action, maxRetries } = currentNode;
  const retryInterval: RetryInterval = currentNode.retryInterval || {
    days: 0,
    hours: 0,
    minutes: 0,
  };

  const handleActionChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isDeclineRecurringAction(event.target.value)) {
      onChange({
        ...currentNode,
        action: event.target.value,
        retryInterval: {
          days: 0,
          hours: 0,
          minutes: 0,
        },
        maxRetries: 0,
      });
      return;
    }

    onChange({
      ...currentNode,
      action: (event.target as HTMLInputElement).value,
    });
  };

  const handleRetryIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number((event.target as HTMLInputElement).value);

    if (Number.isNaN(value)) return;
    if (value < 0) {
      toast.error('Retry interval cannot be negative');
      return;
    }

    onChange({
      ...currentNode,
      retryInterval: {
        days: selectedRetryInterval === IntervalValues.days ? value : 0,
        hours: selectedRetryInterval === IntervalValues.hours ? value : 0,
        minutes: selectedRetryInterval === IntervalValues.minutes ? value : 0,
      },
    });
  };

  const handleMaxRetriesChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (Number((event.target as HTMLInputElement).value) < 0) {
      toast.error('Max retries cannot be negative');
      return;
    }

    onChange({
      ...currentNode,
      maxRetries: Number((event.target as HTMLInputElement).value),
    });
  };

  const handleIntervalTypeChange = (event: SelectChangeEvent) => {
    setSelectedRetryInterval((event.target as HTMLInputElement).value);
  };

  return (
    <div className={s.recurringConfigWrapper}>
      <div className={s.actionBlock}>
        <p className={s.actionTitle}>Action:</p>
        <div>
          <RadioGroup
            className={s.radioButtonGroup}
            value={action || ''}
            onChange={handleActionChange}>
            {ACTION_OPTIONS.map((option) => (
              <FormControlLabel
                className={s.radioButtonSelect}
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </div>
      </div>
      <div className={s.actionBlock}>
        <p className={s.actionTitle}>Retry every:</p>
        <div className={s.retryIntervalContainer}>
          <TextField
            size="small"
            type="number"
            disabled={isDisabled}
            value={
              retryInterval[selectedRetryInterval as keyof RetryInterval] === 0
                ? ''
                : retryInterval[selectedRetryInterval as keyof RetryInterval]
            }
            className={s.flowEditActionBarNameInput}
            onChange={handleRetryIntervalChange}
            required
          />
          <Select
            size="small"
            disabled={isDisabled}
            value={selectedRetryInterval}
            onChange={handleIntervalTypeChange}>
            {RETRY_INTERVAL_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      <div className={s.actionBlock}>
        <p className={s.actionTitle}>Max retries:</p>
        <div>
          <TextField
            aria-valuemin={10}
            size="small"
            type="number"
            disabled={isDisabled}
            value={maxRetries === 0 ? '' : maxRetries}
            className={s.flowEditActionBarNameInput}
            onChange={handleMaxRetriesChange}
            required
          />
        </div>
      </div>
    </div>
  );
}

export default RecurringActionNodeConfiguration;
